/**
 * @ngdoc directive
 * @name runner.library.tabs.procedures
 * @module flowingly.runner.library
 * @description  This component is the SOPs tab of the Library component
 * @usage
 */

import { SharedAngular } from '@Client/@types/sharedAngular';
import IProcedureOverview from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureOverview';
import {
  DeleteEntityTypes,
  FlowinglyPermissions
} from '@Shared.Angular/flowingly.services/flowingly.constants';
import {
  IDeferred,
  IFilterDate,
  IFilterService,
  IPromise,
  IQService,
  IScope
} from 'angular';
import { IStateService } from 'angular-ui-router';
import { ICopyProcedureResult } from './runner.library.procedures.copy.dialog.controller';
import { PublicType } from '@Shared.Angular/@types/core/contracts/constants/publicType';
import { IProcedureSharingResult } from './runner.library.procedures.sharing.dialog.controller';
import { Guid } from '@Shared.Angular/@types/guid';
import {
  IProcedurePublishedMessage,
  IProcedureUpdatedMessage,
  PubSubEvent
} from '@Shared.Angular/@types/pubSub';
import IProcedureTagSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureTagSummary';

declare interface ILibraryProcedureOverview extends IProcedureOverview {
  updatedDateLocal: Date;
  updatedDateTimeLocal: string;
  status: string;
  tagIds: Guid[];
}
declare const moment: Moment;

angular
  .module('flowingly.runner.library')
  .component('flowLibraryTabsProcedures', {
    templateUrl:
      'Client/runner.library/runner.library.procedures/runner.library.tabs.procedures.tmpl.html',
    controller: [
      '$state',
      '$filter',
      '$scope',
      'procedureApiService',
      'dialogService',
      'notificationService',
      'permissionsService',
      'categoryApiService',
      'pubsubService',
      '$q',
      'APP_CONFIG',
      function (
        $state: IStateService,
        $filter: IFilterService,
        $scope: IScope,
        procedureApiService: SharedAngular.ProcedureApiService,
        dialogService: SharedAngular.DialogService,
        notificationService: SharedAngular.NotificationService,
        permissionsService: SharedAngular.PermissionsService,
        categoryApiService: SharedAngular.CategoryApiService,
        pubsubService: SharedAngular.PubSubService,
        $q: IQService,
        APP_CONFIG: SharedAngular.APP_CONFIG
      ) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const $ctrl: {
          procedures: ILibraryProcedureOverview[];
          filteredProcedures: ILibraryProcedureOverview[];
          setProcedureStatus: (procedure: ILibraryProcedureOverview) => void;
          setLibraryProperties: (procedure: ILibraryProcedureOverview) => void;
          onTagFilterChange: (selectedTagIds: Guid[]) => void;
          uniqueCreatorNames: IDeferred<string[]>;
          uniqueOwnerNames: IDeferred<string[]>;
          [key: string]: unknown;
        } = this;
        $ctrl.procedures = [];
        $ctrl.filteredProcedures = [];
        $ctrl.uniqueCreatorNames = $q.defer<string[]>();
        $ctrl.uniqueOwnerNames = $q.defer<string[]>();
        $ctrl.options = {
          dataSource: {
            data: $ctrl.filteredProcedures,
            pageSize: 15,
            schema: {
              model: {
                fields: {
                  updatedDateLocal: { type: 'date' }
                }
              }
            },
            sort: {
              field: 'updatedDateLocal',
              dir: 'desc',
              compare: dateTimeCompare
            }
          },
          noRecords: {
            template: '<flow-library-no-results></flow-library-no-results>'
          },
          scrollable: true,
          pageable: true,
          sortable: true,
          resizable: true,
          filterable: {
            mode: 'row',
            operators: {
              date: {
                gte: 'Is after or equal to',
                lte: 'Is before or equal to'
              }
            }
          },
          columns: getColumns()
        };
        const categories = categoryApiService.cachedCategories;

        const categoriesWithSubCategories = categories.map(
          convertIntoHierarchicalDataStructure
        );

        const dataPromises: IPromise<unknown>[] = [
          procedureApiService.getProcedures()
        ];

        if (APP_CONFIG.enableTagging) {
          dataPromises.push(procedureApiService.getUsedTagsInProcedures());
        }

        Promise.all(dataPromises).then((results) => {
          const procedures = results[0] as IProcedureOverview[];
          $ctrl.procedures = procedures as ILibraryProcedureOverview[];
          $ctrl.uniqueCreatorNames.resolve([
            ...new Set(
              $ctrl.procedures.map((procedure) => procedure.creatorName)
            )
          ]);
          $ctrl.uniqueOwnerNames.resolve([
            ...new Set(
              $ctrl.procedures
                .filter((p) => p.ownerName)
                .map((p) => p.ownerName)
            )
          ]);

          for (const procedure of $ctrl.procedures) {
            $ctrl.setLibraryProperties(procedure);
          }
          const procedureTagSummaries = results[1] as IProcedureTagSummary[];
          if (APP_CONFIG.enableTagging && procedureTagSummaries) {
            $ctrl.usedTagIds = [
              ...new Set(procedureTagSummaries.flatMap((tag) => tag.tagIds))
            ];
            for (const procedureTags of procedureTagSummaries) {
              const procedure = $ctrl.procedures.find(
                (p) => p.id === procedureTags.procedureId
              );
              if (procedure) {
                procedure.tagIds = procedureTags.tagIds;
              }
            }
          }

          $ctrl.noRows = !$ctrl.procedures || $ctrl.procedures.length === 0;
          $ctrl.gridInitDone = true;
          $ctrl.filteredProcedures = [...$ctrl.procedures];
          $scope.libraryGrid.dataSource.data($ctrl.filteredProcedures);
          $scope.libraryGrid.refresh();
        });

        const subscriberId = 'flowingly.runner.library.procedures';
        pubsubService.subscribe(
          'PROCEDURE_DELETED',
          (event: PubSubEvent, procedureId: Guid) => {
            $ctrl.procedures = $ctrl.procedures.filter(
              (procedure) => procedure.id !== procedureId
            );
            $ctrl.noRows = $ctrl.procedures.length === 0;
            $ctrl.filteredProcedures = [...$ctrl.procedures];
            $scope.libraryGrid.dataSource.data($ctrl.filteredProcedures);
            $scope.libraryGrid.refresh();
          },
          subscriberId
        );

        pubsubService.subscribeMany(
          ['PROCEDURE_UPDATED', 'PROCEDURE_PUBLISHED'],
          updateProcedure,
          subscriberId
        );

        function updateProcedure(
          event: PubSubEvent,
          message: IProcedurePublishedMessage | IProcedureUpdatedMessage
        ) {
          const updatedProcedure = message.overview;
          const existingProcedure = $ctrl.procedures.find(
            (p) => p.id === updatedProcedure.id
          );
          if (!existingProcedure) {
            return;
          }
          Object.assign(existingProcedure, updatedProcedure);
          $ctrl.setLibraryProperties(existingProcedure);
          $scope.libraryGrid.dataSource.data($ctrl.filteredProcedures);
          $scope.libraryGrid.refresh();
        }

        $scope.$on('$destroy', () => {
          pubsubService.unsubscribeAll(subscriberId);
        });

        function convertIntoHierarchicalDataStructure(node) {
          const convertedNode = {
            id: node.id,
            name: node.name,
            items: node.subCategories.map(convertIntoHierarchicalDataStructure)
          };
          return convertedNode;
        }

        function getColumns(): kendo.ui.GridColumn[] {
          return [
            {
              field: 'name',
              title: 'Name',
              filterable: {
                cell: {
                  operator: 'contains',
                  suggestionOperator: 'contains',
                  showOperators: false,
                  template: function (args) {
                    args.element.kendoAutoComplete({
                      dataSource: new kendo.data.DataSource({
                        data: $ctrl.filteredProcedures
                      }),
                      dataTextField: 'name',
                      placeholder: 'Search by Name',
                      valuePrimitive: true,
                      filter: 'contains',
                      autoWidth: true
                    });
                  }
                }
              }
            },
            {
              field: 'creatorName',
              title: 'Created By',
              filterable: {
                cell: {
                  operator: 'contains',
                  showOperators: false,
                  template: function (args) {
                    $ctrl.uniqueCreatorNames.promise.then((creatorNames) => {
                      args.element.kendoComboBox({
                        dataSource: creatorNames,
                        placeholder: 'Search by Creator',
                        valuePrimitive: true,
                        autoWidth: true
                      });
                    });
                  }
                }
              }
            },
            {
              field: 'ownerName',
              title: 'Owner',
              filterable: {
                cell: {
                  operator: 'contains',
                  showOperators: false,
                  template: function (args) {
                    $ctrl.uniqueOwnerNames.promise.then((ownerNames) => {
                      args.element.kendoComboBox({
                        dataSource: ownerNames,
                        placeholder: 'Search by Owner',
                        valuePrimitive: true,
                        autoWidth: true
                      });
                    });
                  }
                }
              }
            },
            {
              field: 'updatedDateLocal',
              title: 'Last Updated',
              sortable: {
                compare: dateTimeCompare
              },
              template: (procedure: ILibraryProcedureOverview) => {
                return (
                  '<span title="' +
                  procedure.updatedDateTimeLocal +
                  '">' +
                  moment(procedure.updatedDateLocal).format('DD/MM/YYYY') +
                  '</span>'
                );
              },
              filterable: {
                cell: {
                  template: function (args) {
                    args.element
                      .kendoDatePicker({
                        placeholder: 'Search by Last Updated Date',
                        format: 'dd/MM/yyyy'
                      })
                      .attr('placeholder', 'Search by Last Updated Date');
                  }
                }
              }
            },
            {
              field: 'categoryName',
              title: 'Category',
              sortable: true,
              filterable: {
                cell: {
                  showOperators: false,
                  template: function (args) {
                    args.element.kendoDropDownTree({
                      dataSource: categoriesWithSubCategories,
                      dataTextField: 'name',
                      dataValueField: 'id', //this needs to be here for the selected value to display in the select list
                      placeholder: 'Search by Category',
                      filter: 'contains',
                      valuePrimitive: true,
                      ignoreCase: true,
                      autoWidth: true,
                      change: function (e) {
                        if (this.value() === '') {
                          $scope.libraryGrid.dataSource.filter({});
                        } else {
                          const filter = {
                            logic: 'or',
                            filters: [
                              {
                                field: 'categoryId',
                                operator: 'eq',
                                value: this.value()
                              }
                            ]
                          };
                          $scope.libraryGrid.dataSource.filter(filter);
                        }
                      }
                    });
                  }
                }
              }
            },
            {
              field: 'viewCount',
              title: 'Views',
              filterable: false,
              width: 100,
              headerTemplate: () => {
                return '<span title="The number of times this SOP has been viewed in a Step form">Views</span>';
              }
            },
            {
              field: 'publishedTo',
              title: 'Published To',
              sortable: true,
              filterable: false
            },
            {
              field: 'status',
              title: 'Status',
              width: 180,
              template:
                '<library-status-badge status="dataItem.status"></library-status-badge>',
              filterable: {
                cell: {
                  showOperators: false,
                  template: function (args) {
                    args.element.kendoComboBox({
                      dataSource: [
                        { text: 'All', value: '' },
                        { text: 'Draft' },
                        { text: 'Internal' },
                        { text: 'Public' },
                        { text: 'Public SSO' }
                      ],
                      dataTextField: 'text',
                      dataValueField: 'value', //this needs to be here for the selected value to display in the select list
                      placeholder: 'Search by Status'
                    });
                  }
                }
              }
            },
            {
              title: 'Actions',
              width: '160px',
              filterable: false,
              template: (procedure: ILibraryProcedureOverview) => {
                return (
                  '<span class="flow-library-list-actions list-actions">' +
                  (permissionsService.currentUserHasPermission(
                    FlowinglyPermissions.PROCEDURE_UPDATE
                  ) && procedure.published
                    ? '<span ng-click="$ctrl.openSharingModal(dataItem)" class="list-actions__action">'
                    : '<span class="list-actions__action list-action-disabled">') +
                  '<i class="fa-light fa-lock-keyhole" title="Edit Sharing"></i></span>' +
                  (permissionsService.currentUserHasPermission(
                    FlowinglyPermissions.PROCEDURE_UPDATE
                  )
                    ? '<span ng-click="$ctrl.editProcedure(dataItem)" class="list-actions__action">'
                    : '<span class="list-actions__action list-action-disabled">') +
                  '<i class="fa-light fa-pencil" title="Edit"></i></span>' +
                  (permissionsService.currentUserHasPermission(
                    FlowinglyPermissions.PROCEDURE_CREATE
                  )
                    ? '<span ng-click="$ctrl.copyProcedure(dataItem)" class="list-actions__action">'
                    : '<span class="list-actions__action list-action-disabled">') +
                  '<i class="fa-light fa-copy" title="Copy"></i></span>' +
                  (APP_CONFIG.enableTagging
                    ? '<span ng-click="$ctrl.showTagsModal(dataItem)" class="list-actions__action">' +
                      '<i class="fa-light fa-tag" title="View tags"></i></span>'
                    : '') +
                  (permissionsService.currentUserHasPermission(
                    FlowinglyPermissions.PROCEDURE_DELETE
                  )
                    ? '<span ng-click="$ctrl.deleteProcedure(dataItem)" class="list-actions__action">'
                    : '<span class="list-actions__action list-action-disabled">') +
                  '<i class="fa-light fa-trash-can" title="Delete"></i></span>' +
                  '</span>'
                );
              }
            }
          ];
        }

        function dateTimeCompare(
          a: ILibraryProcedureOverview,
          b: ILibraryProcedureOverview
        ) {
          if (a.updatedDateTimeLocal < b.updatedDateTimeLocal) {
            return -1;
          }
          if (a.updatedDateTimeLocal > b.updatedDateTimeLocal) {
            return 1;
          }
          return 0;
        }

        this.editProcedure = (procedure: IProcedureOverview) => {
          $state.go(
            'app.runner.procedure',
            { procedureId: procedure.id, edit: true },
            {}
          );
        };

        this.showTagsModal = (procedure: IProcedureOverview) => {
          $ctrl.tagsModalProcedure = procedure;
        };

        this.onTagsModalClose = () => {
          $ctrl.tagsModalProcedure = null;
          $scope.$apply();
        };

        this.deleteProcedure = (procedure: IProcedureOverview) => {
          dialogService
            .showDialog({
              template:
                'Client/runner.setup/runner.setup.delete/runner.setup.delete.dialog.tmpl.html',
              controller: 'setupDeleteDialogController',
              appendClassName: 'ngdialog-normal',
              scope: $scope,
              data: {
                itemToDelete: procedure,
                entityType: DeleteEntityTypes.procedure
              }
            })
            .then((result) => {
              if (dialogService.isCloseModalWithCancelAction(result)) {
                //user closed modal by clicking on overlay (or cancel or press Esc key)
                return;
              }

              procedureApiService
                .deleteProcedure(procedure.id)
                .then(() => {
                  notificationService.showSuccessToast('SOP Deleted');
                  $ctrl.procedures = [
                    ...$ctrl.procedures.filter((p) => p.id !== procedure.id)
                  ];
                  $ctrl.noRows = $ctrl.procedures.length === 0;
                  $ctrl.filteredProcedures = [...$ctrl.procedures];
                  $scope.libraryGrid.dataSource.data($ctrl.filteredProcedures);
                  $scope.libraryGrid.refresh();
                })
                .catch(() =>
                  notificationService.showErrorToast('Failed to delete SOP')
                );
            });
        };

        this.copyProcedure = (procedure: IProcedureOverview) => {
          dialogService
            .showDialog({
              template:
                'Client/runner.library/runner.library.procedures/runner.library.procedures.copy.dialog.tmpl.html',
              controller: 'procedureCopyDialogController',
              appendClassName: 'ngdialog-normal',
              scope: $scope,
              data: {
                procedure: procedure
              }
            })
            .then((result: ICopyProcedureResult) => {
              if (dialogService.isCloseModalWithCancelAction(result)) {
                return;
              }

              $state.go('app.runner.procedure', {
                procedureId: result.id
              });
            });
        };

        this.openSharingModal = (procedure: IProcedureOverview) => {
          dialogService
            .showDialog({
              template:
                'Client/runner.library/runner.library.procedures/runner.library.procedures.sharing.dialog.tmpl.html',
              controller: 'procedureSharingDialogController',
              appendClassName: 'ngdialog-normal',
              scope: $scope,
              data: {
                procedure: procedure
              }
            })
            .then((result: IProcedureSharingResult) => {
              if (dialogService.isCloseModalWithCancelAction(result)) {
                return;
              }

              procedureApiService
                .updateProcedurePublicType(result.id, result.publicType)
                .then(() => {
                  const targetProcedure = $ctrl.procedures.find(
                    (p) => p.id === result.id
                  );
                  targetProcedure.publicType = result.publicType;
                  $ctrl.setProcedureStatus(targetProcedure);
                  $scope.libraryGrid.dataSource.data($ctrl.filteredProcedures);
                  $scope.libraryGrid.refresh();
                  notificationService.showSuccessToast(
                    `${procedure.name} Sharing Updated`
                  );
                });
            });
        };

        this.setLibraryProperties = (procedure: ILibraryProcedureOverview) => {
          procedure.updatedDateLocal = new Date(
            $filter<IFilterDate>('utcToLocal')(
              procedure.updatedDate,
              'yyyy-MM-dd'
            )
          );
          procedure.updatedDateTimeLocal = $filter<IFilterDate>('utcToLocal')(
            procedure.updatedDate,
            'yyyy-MM-dd HH:mm:ss'
          );
          $ctrl.setProcedureStatus(procedure);
        };

        this.setProcedureStatus = (procedure: ILibraryProcedureOverview) => {
          procedure.status =
            procedure.published === false
              ? 'Draft'
              : procedure.publicType === null
              ? 'Internal'
              : procedure.publicType === PublicType.Public
              ? 'Public'
              : 'Public SSO';
        };

        this.onTagFilterChange = (selectedTagIds: Guid[] = []) => {
          if (!selectedTagIds || selectedTagIds.length === 0) {
            $ctrl.filteredProcedures = [...$ctrl.procedures];
            $scope.libraryGrid.dataSource.data($ctrl.filteredProcedures);
            return;
          }

          $ctrl.filteredProcedures = $ctrl.procedures.filter(
            (procedure) =>
              procedure.tagIds &&
              procedure.tagIds.some((tagId) => selectedTagIds.includes(tagId))
          );
          $scope.libraryGrid.dataSource.data($ctrl.filteredProcedures);
        };
      }
    ]
  });
